/**
 * P2-31 (pacote 2 · E6): blocos da Ficha da Oportunidade a partir da Proposta vigente — 4 indicadores, cartão
 * "Proposta vigente" e "Fornecedores e faturamento" recolhido. Só apresentação: os mesmos cálculos do pacote 1
 * (economiaVersao, calculoLocacao, calculoTradein, resumoLinhas, locaisDaVersao), sem repetir status ou validade.
 */
import type { ReactNode } from 'react';
import { Ajuda, SeloSituacao, type Indicador } from '../ui.tsx';
import { Icone } from '../../componentes/icones.tsx';
import {
  SITUACOES_VERSAO,
  calculoLocacao,
  calculoTradein,
  economiaVersao,
  entidadeDe,
  gruposDaVersao,
  locaisDaVersao,
  ofertaSelecionada,
  parceiroSelecionado,
  rascunhoDe,
  resumoLinhas,
  versaoVigente,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import type { DadosProposta } from './contexto.tsx';
import { moeda2, pct, pctNum } from './formatos.ts';

/** Os 4 indicadores do topo da Ficha (por tipo); sem proposta, o valor estimado. */
export function indicadoresDaFicha(dados: DadosProposta | null, valorEstimado: number | null): Indicador[] {
  const p = dados?.proposta;
  if (!dados || !p) return [{ rotulo: 'Proposta', valor: 'Sem proposta ainda', icone: 'documento' }, { rotulo: 'Valor estimado', valor: valorEstimado === null ? 'Não determinado na origem' : moeda2(valorEstimado), icone: 'grafico' }];
  const d = dados.base;
  const v = versaoVigente(p);
  if (p.tipo === 'venda') {
    const e = economiaVersao(d, v);
    return [
      { rotulo: 'Valor de venda', valor: moeda2(e.totalProdutos), detalhe: e.totalFinal !== e.totalProdutos ? `Total final ao cliente (com frete) ${moeda2(e.totalFinal)}` : undefined, icone: 'grafico' },
      { rotulo: 'Custo', valor: moeda2(e.custo), icone: 'produtos' },
      { rotulo: 'Margem (R$)', valor: moeda2(e.bruta), icone: 'barras' },
      { rotulo: 'Margem (%)', valor: pct(e.percentual), icone: 'pizza' },
    ];
  }
  if (p.tipo === 'locacao') {
    const loc = calculoLocacao(v);
    const of = ofertaSelecionada(v);
    if (!loc || !of) return [{ rotulo: 'Proposta de parceiro', valor: 'nenhuma selecionada', icone: 'documento' }];
    return [
      { rotulo: 'Valor da parcela', valor: moeda2(loc.clienteParcela), detalhe: `${of.periodicidade.toLowerCase()} · Prazo contratual ${of.prazoMeses} meses · ${loc.cobrancas} cobrança(s)`, icone: 'dinheiro' },
      { rotulo: 'Valor total do contrato', valor: moeda2(loc.totalContrato), icone: 'grafico' },
      {
        rotulo: 'Comissão bruta',
        valor: moeda2(loc.comissaoTotal),
        icone: 'barras',
        ajuda: <Ajuda titulo="Comissão bruta">Total previsto do contrato (margem por parcela × cobranças). Não é o valor mensal nem o valor já recebido: o recebimento da Inttrix é registrado no acompanhamento da OL.</Ajuda>,
      },
      { rotulo: 'Líquido total do parceiro', valor: moeda2(loc.liquidoTotal), icone: 'pizza' },
    ];
  }
  const tri = calculoTradein(v);
  if (!tri) return [{ rotulo: 'Avaliação de comprador', valor: 'nenhuma selecionada', icone: 'documento' }];
  return [
    { rotulo: 'Avaliação bruta total', valor: moeda2(tri.bruto), icone: 'grafico' },
    { rotulo: 'Valor total destinado ao Cliente', valor: moeda2(tri.oferta), icone: 'dinheiro' },
    { rotulo: 'Comissão total Inttrix', valor: moeda2(tri.comissao), icone: 'barras' },
    { rotulo: 'Percentual equivalente', valor: pctNum(tri.percentualEquivalente), icone: 'pizza' },
  ];
}

/** Cartão "Proposta vigente": número, versão, situação, linhas (resultado) e validade; ações. */
export function CartaoPropostaVigente({ dados, acoes }: { dados: DadosProposta | null; acoes?: ReactNode }) {
  const p = dados?.proposta;
  if (!dados || !p)
    return (
      <section className="cartao-ficha cartao-proposta-vigente" data-teste="painel-principal" aria-label="Proposta vigente">
        <h3>
          <Icone nome="documento" /> Proposta vigente
        </h3>
        <p className="texto-secundario sem-margem">Sem proposta ainda. Crie a proposta na aba Propostas.</p>
        {acoes && <div className="acoes-linha">{acoes}</div>}
      </section>
    );
  const v = versaoVigente(p);
  const r = resumoLinhas(v);
  const ras = rascunhoDe(p);
  const vigente = p.versoes.filter((x) => x.situacao !== 'rascunho' && x.situacao !== 'substituida').at(-1)?.numero === v.numero;
  const partes = [`${r.linhas} ${r.linhas === 1 ? 'item' : 'itens'}`, r.ganhos ? `${r.ganhos} ganho(s)` : '', r.parciais ? `${r.parciais} parcial(is)` : '', r.perdidos ? `${r.perdidos} perdido(s)` : '', r.pendentes ? `${r.pendentes} pendente(s)` : ''].filter(Boolean);
  return (
    <section className="cartao-ficha cartao-proposta-vigente" data-teste="painel-principal" aria-label="Proposta vigente">
      <h3>
        <Icone nome="documento" /> Proposta vigente
      </h3>
      <div className="linha-proposta-vigente">
        <strong className="numero-proposta">{p.numero}</strong>
        <span className="selo selo-neutro">
          v{v.numero}
          {vigente ? ' · vigente' : ''}
        </span>
        <SeloSituacao texto={SITUACOES_VERSAO[v.situacao]} tom={v.situacao === 'substituida' ? 'encerrado' : v.situacao.startsWith('aceita') ? 'positivo' : 'aberto'} />
      </div>
      <p className="texto-secundario sem-margem">
        <span data-teste="vigente-linhas" title={`${r.ganhos} ganha(s) · ${r.parciais} parcial(is) · ${r.perdidos} perdida(s) · ${r.pendentes} pendente(s)`}>
          {partes.join(' · ')}
        </span>{' '}
        · Validade {dataBr(v.validade)}
      </p>
      {ras && ras.numero !== v.numero && <p className="texto-secundario sem-margem">v{ras.numero} em elaboração (rascunho) — ainda não é a vigente.</p>}
      {acoes && <div className="acoes-linha">{acoes}</div>}
    </section>
  );
}

/** "Fornecedores e faturamento" recolhido (partes envolvidas da vigente), com o resumo numa linha. */
export function FornecedoresFaturamento({ dados }: { dados: DadosProposta | null }) {
  const p = dados?.proposta;
  if (!dados || !p) return null;
  const d = dados.base;
  const v = versaoVigente(p);
  const nomes = (ids: string[]) => ids.map((x) => d.grupos.find((y) => y.id === x)?.nome ?? '—').join(', ') || '—';
  let pares: [string, ReactNode][];
  let resumo: string;
  if (p.tipo === 'venda') {
    const g = gruposDaVersao(d, v);
    const fat = locaisDaVersao(d, v).faturamento;
    const parceiros = nomes(g.selecionados.length ? g.selecionados : g.cotados);
    pares = [
      ['Parceiro(s) comercial(is)', parceiros],
      ['Entidade(s) faturadora(s)', [...new Set(v.itens.filter((i) => i.entidadeId).map((i) => entidadeDe(d, i.entidadeId)?.fantasia ?? '—'))].join(', ') || '—'],
      ['Faturar para', `${fat.razao} (${fat.cnpj})`],
    ];
    resumo = `${parceiros} · faturar para ${fat.razao}`;
  } else {
    const parceiro = parceiroSelecionado(v);
    const ent = parceiro ? entidadeDe(d, parceiro) : undefined;
    pares = [
      [p.tipo === 'locacao' ? 'Parceiro de Locação selecionado' : 'Parceiro de Trade-in selecionado', ent ? <span data-teste="painel-parceiro-selecionado">{`${ent.razao} — ${ent.cnpj}`}</span> : 'nenhum selecionado'],
      ['Parceiro comercial', ent ? (d.grupos.find((x) => x.id === ent.grupoId)?.nome ?? '—') : '—'],
    ];
    resumo = ent ? ent.razao : 'nenhum parceiro selecionado';
  }
  return (
    <details className="fornecedores-faturamento" data-teste="painel-partes">
      <summary>
        <Icone nome="grupo" /> <span className="titulo-recolhido">Fornecedores e faturamento</span> <span className="texto-secundario">· {resumo}</span>
      </summary>
      <dl className="grade-dados">
        {pares.map(([k, x]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{x}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
